import * as yup from 'yup'

/**
 * Проверка адреса почты.
 *
 * Встроенная в yup `.email()` слишком мягкая: она пропускает `ivan@mail` без
 * домена верхнего уровня, `ivan@localhost`, однобуквенную зону `a@b.c` и точку
 * по краям имени (`.ivan@mail.ru`). На таких адресах письмо с кодом просто не
 * доходит, а человек остаётся у экрана ввода кода и не понимает, почему.
 *
 * Здесь: имя без точек по краям и без двойных точек, домен из нормальных
 * меток, зона минимум из двух букв.
 */
const EMAIL_PATTERN =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i

/** Ограничения RFC 5321: имя до 64 символов, весь адрес до 254. */
const LOCAL_PART_MAX = 64
const EMAIL_MAX = 254

const EMAIL_HINT = 'Некорректный email. Например: ivan@mail.ru'

/**
 * Поле email: обрезаем пробелы и приводим к нижнему регистру, затем проверяем.
 * Нормализация до проверки нужна, чтобы «Ivan@Mail.RU » с хвостом пробелов не
 * считался ошибкой — люди регулярно копируют адрес вместе с ними.
 */
export const emailField = yup
  .string()
  .trim()
  .lowercase()
  .required('Укажите email')
  .max(EMAIL_MAX, EMAIL_HINT)
  .test(
    'local-part-length',
    EMAIL_HINT,
    value => !value || value.split('@')[0].length <= LOCAL_PART_MAX
  )
  .test('format', EMAIL_HINT, value => !value || EMAIL_PATTERN.test(value))

/** Схема формы ввода email без согласий (логин существующего юзера и привязка). */
export const emailOnlySchema = yup.object({
  email: emailField
})

/** Схема регистрации: email + 3 чекбокса (оферта и ПД обязательные, реклама — нет). */
export const registrationSchema = yup.object({
  email: emailField,
  agreedOffer: yup
    .boolean()
    .oneOf([true], 'Необходимо согласие с офертой')
    .required(),
  agreedPersonalData: yup
    .boolean()
    .oneOf([true], 'Необходимо согласие на обработку персональных данных')
    .required(),
  agreedAds: yup.boolean().required()
})

export type EmailOnlyValues = yup.InferType<typeof emailOnlySchema>
export type RegistrationValues = yup.InferType<typeof registrationSchema>
